import React, { ChangeEvent, useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { deleteObject, ref as storageRef, uploadBytes } from "firebase/storage";
import { BookOpenCheck, CheckCircle2, Eye, EyeOff, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { db, storage } from "../../Lib/Firebase";

type Material = {
    id: string;
    title: string;
    description: string;
    category: string;
    fileName: string;
    storagePath: string;
    sizeBytes: number;
    status: "draft" | "published";
};

const MAX_PDF_SIZE = 25 * 1024 * 1024;

export default function StudyMaterialsTab() {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("Internship Learning");
    const [file, setFile] = useState<File | null>(null);
    const [publishing, setPublishing] = useState(false);
    const [busyId, setBusyId] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        return onSnapshot(collection(db, "materials"), (snapshot) => {
            setMaterials(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Material)));
        }, (loadError) => setError(loadError.message));
    }, []);

    const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] || null;
        event.target.value = "";
        setError("");
        if (!selected) return;
        if (selected.type !== "application/pdf" && !selected.name.toLowerCase().endsWith(".pdf")) {
            setError("Only PDF files can be published as paid study material.");
            return;
        }
        if (selected.size > MAX_PDF_SIZE) {
            setError("The PDF must be 25 MB or smaller.");
            return;
        }
        setFile(selected);
    };

    const uploadMaterial = async () => {
        if (!title.trim() || !file) {
            setError("Add a title and choose a PDF before publishing.");
            return;
        }
        setPublishing(true);
        setError("");
        setMessage("");
        let uploadedPath = "";
        try {
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
            const materialRef = doc(collection(db, "materials"));
            uploadedPath = `materials/${materialRef.id}/${Date.now()}-${safeName}`;
            await uploadBytes(storageRef(storage, uploadedPath), file, { contentType: "application/pdf" });
            await setDoc(materialRef, {
                title: title.trim(),
                description: description.trim(),
                category: category.trim() || "Internship Learning",
                fileName: file.name,
                storagePath: uploadedPath,
                sizeBytes: file.size,
                access: "signed-in-students",
                status: "published",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setTitle("");
            setDescription("");
            setCategory("Internship Learning");
            setFile(null);
            setMessage("PDF published for students with verified paid-course access.");
        } catch (uploadError: any) {
            if (uploadedPath) await deleteObject(storageRef(storage, uploadedPath)).catch(() => undefined);
            setError(uploadError?.message || "The material could not be published.");
        } finally {
            setPublishing(false);
        }
    };

    const toggleStatus = async (material: Material) => {
        setBusyId(material.id);
        setError("");
        try {
            const status = material.status === "published" ? "draft" : "published";
            await updateDoc(doc(db, "materials", material.id), { status, updatedAt: serverTimestamp() });
            setMessage(`${material.title} is now ${status}.`);
        } catch (statusError: any) {
            setError(statusError?.message || "The publishing status could not be changed.");
        } finally {
            setBusyId("");
        }
    };

    const removeMaterial = async (material: Material) => {
        if (!window.confirm(`Delete ${material.title}? This removes the PDF from every student profile.`)) return;
        setBusyId(material.id);
        setError("");
        try {
            if (material.storagePath) await deleteObject(storageRef(storage, material.storagePath)).catch(() => undefined);
            await deleteDoc(doc(db, "materials", material.id));
            setMessage(`${material.title} deleted.`);
        } catch (deleteError: any) {
            setError(deleteError?.message || "The material could not be deleted.");
        } finally {
            setBusyId("");
        }
    };

    return <div className="space-y-5">
        <section className="rounded-3xl border border-blue-100 bg-white p-5 shadow-xl shadow-blue-900/5 sm:p-7">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start"><div><span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-blue-700"><BookOpenCheck className="h-3.5 w-3.5" />Student learning library</span><h2 className="mt-3 text-2xl font-black text-[#061a4a]">Publish study materials</h2><p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">Published PDFs appear automatically for students with verified paid-course access.</p></div><span className="w-fit rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">{materials.filter((item) => item.status === "published").length} published</span></div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Field label="Material title"><input aria-label="Material title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} placeholder="Example: Week 1 — AI Foundations" /></Field>
                <Field label="Category"><input aria-label="Material category" value={category} onChange={(event) => setCategory(event.target.value)} maxLength={60} /></Field>
                <Field label="Description" wide><textarea aria-label="Material description" value={description} onChange={(event) => setDescription(event.target.value)} rows={3} maxLength={300} placeholder="Explain what the student will learn from this PDF." /></Field>
                <div className="sm:col-span-2"><span className="mb-2 block text-sm font-bold text-slate-700">PDF file</span><label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/60 px-5 py-7 text-center transition hover:border-blue-400 hover:bg-blue-50"><Upload className="h-7 w-7 text-blue-600" /><span className="mt-2 font-black text-slate-900">{file ? file.name : "Choose a PDF"}</span><span className="mt-1 text-xs text-slate-500">PDF only • Maximum 25 MB</span><input aria-label="Upload paid study material PDF" type="file" accept="application/pdf,.pdf" className="sr-only" onChange={chooseFile} /></label></div>
            </div>
            {message && <p role="status" className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" />{message}</p>}
            {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</p>}
            <button type="button" disabled={publishing || !title.trim() || !file} onClick={uploadMaterial} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#075dcc] to-[#38aee8] px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-500/20 disabled:opacity-50">{publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{publishing ? "Publishing PDF…" : "Publish to paid students"}</button>
        </section>

        <section className="rounded-3xl border border-blue-100 bg-white p-5 shadow-xl shadow-blue-900/5 sm:p-7">
            <h3 className="text-xl font-black text-[#061a4a]">Published library</h3><p className="mt-1 text-sm text-slate-500">Unpublish a material to hide it without deleting its PDF.</p>
            <div className="mt-5 space-y-3">{materials.map((material) => <article key={material.id} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:flex-row sm:items-center sm:justify-between"><div className="flex min-w-0 items-start gap-3"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-100 text-blue-700"><FileText className="h-5 w-5" /></span><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h4 className="font-black text-slate-900">{material.title}</h4><span className={`rounded-full px-2 py-0.5 text-[11px] font-black uppercase ${material.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{material.status}</span></div><p className="mt-1 text-sm text-slate-500">{material.category} • {formatSize(material.sizeBytes)}</p><p className="mt-1 truncate text-xs text-slate-400">{material.fileName}</p></div></div><div className="flex shrink-0 flex-wrap gap-2"><button type="button" disabled={busyId === material.id} onClick={() => toggleStatus(material)} className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-sm font-bold text-blue-700 disabled:opacity-50">{material.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{material.status === "published" ? "Unpublish" : "Publish"}</button><button type="button" disabled={busyId === material.id} onClick={() => removeMaterial(material)} className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-bold text-red-600 disabled:opacity-50"><Trash2 className="h-4 w-4" />Delete</button></div></article>)}{materials.length === 0 && <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/40 py-12 text-center"><FileText className="mx-auto h-9 w-9 text-blue-300" /><p className="mt-3 font-bold text-slate-700">No paid materials published yet</p><p className="mt-1 text-sm text-slate-500">Upload the first internship PDF above.</p></div>}</div>
        </section>
    </div>;
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactElement<any> }) {
    return <label className={wide ? "sm:col-span-2" : ""}><span className="mb-2 block text-sm font-bold text-slate-700">{label}</span>{React.cloneElement(children, { className: "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" })}</label>;
}

function formatSize(size = 0) {
    if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
}
